import React, { useState, useEffect } from 'react';
import { crmAPI, leadAPI, projectAPI, collateralAPI, salesEnablementAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { 
  Users, Bell, Plus, CheckCircle2, AlertTriangle, Target, X,
  Calendar as CalendarIcon, Clock, RefreshCw, Settings, List, Kanban, Bot
} from 'lucide-react';

// Import extracted components
import {
  LEAD_SOURCES,
  LeadsListView,
  KanbanBoard,
  LeadDetailView,
  AskEffyChatbot,
  ReminderCard,
  CallAssistantDialog,
  AddLeadDialog,
  ActivityDialog,
  ReminderDialog,
  DispositionDialog,
  ReassignDialog,
  SettingsDialog,
  StageDialog,
  AdminAISettingsDialog,
  StageManagerDialog,
} from '../../components/crm';

const CRMPage = () => {
  const { user, hasRole } = useAuth();
  const [viewMode, setViewMode] = useState('kanban'); // Default to kanban
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [kanbanData, setKanbanData] = useState(null);
  const [escalationSettings, setEscalationSettings] = useState(null);
  const [overdueLeads, setOverdueLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);
  const [leadsPerPage] = useState(20);
  
  // Dialogs
  const [addLeadDialog, setAddLeadDialog] = useState(false);
  const [leadDetailDialog, setLeadDetailDialog] = useState({ open: false, lead: null, details: null });
  const [activityDialog, setActivityDialog] = useState({ open: false, leadId: null });
  const [reminderDialog, setReminderDialog] = useState({ open: false, leadId: null });
  const [dispositionDialog, setDispositionDialog] = useState({ open: false, lead: null });
  const [reassignDialog, setReassignDialog] = useState({ open: false, lead: null });
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [stageDialog, setStageDialog] = useState({ open: false, stage: null, isEdit: false });
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  
  // New CRM feature states
  const [callAssistantDialog, setCallAssistantDialog] = useState({ open: false, lead: null });
  const [apiKeySettingsDialog, setApiKeySettingsDialog] = useState(false);
  const [stageManagerDialog, setStageManagerDialog] = useState(false);
  const [userApiKey, setUserApiKey] = useState(localStorage.getItem('openai_api_key') || '');
  
  // Lead Detail specific state
  const [leadCollaterals, setLeadCollaterals] = useState([]);
  const [leadSalesEnablement, setLeadSalesEnablement] = useState(null);
  const [leadEffyMessages, setLeadEffyMessages] = useState([]);
  const [leadEffyLoading, setLeadEffyLoading] = useState(false);
  
  // Form states
  const [newLead, setNewLead] = useState({
    name: '', phone: '', email: '', source: '', project_id: '',
    bhk_preference: '', budget: '', remarks: '', occupation: '', company: '',
    interest_area: '', pain_points: [], life_events: []
  });
  
  const [newActivity, setNewActivity] = useState({
    activity_type: 'call', title: '', description: '', outcome: '',
    duration_minutes: '', call_type: 'outgoing', phone_used: ''
  });
  
  const [newReminder, setNewReminder] = useState({
    reminder_type: 'follow_up', title: '', description: '', due_date: '', priority: 'medium'
  });

  const [dispositionForm, setDispositionForm] = useState({
    newStatus: '', notes: '', createReminder: false,
    reminderTitle: '', reminderDueDate: '', reminderType: 'follow_up', reminderPriority: 'medium'
  });

  const [reassignForm, setReassignForm] = useState({ newAssigneeId: '', notes: '' });
  
  const [newStage, setNewStage] = useState({
    name: '', color: '#3b82f6', is_win_stage: false, is_lost_stage: false
  });

  const [escalationForm, setEscalationForm] = useState({
    days_before_escalation: 3, send_email: true, send_notification: true, is_active: true
  });

  const isAdmin = hasRole(['super_admin', 'builder_admin']);
  const canReassign = hasRole(['super_admin', 'builder_admin', 'sales_team']);

  useEffect(() => {
    loadData();
    // Note: AI score calculation is now done on-demand, not on page load
    // This improves page load performance significantly
  }, []);

  // Reload leads when filters or page changes
  useEffect(() => {
    if (!loading) {
      loadLeads();
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    if (viewMode === 'kanban') {
      loadKanbanData();
    }
  }, [viewMode]);

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { role: 'user', content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setChatLoading(true);
    
    try {
      const leadId = leadDetailDialog.lead?.id || null;
      const res = await crmAPI.askChatbot(chatInput, leadId);
      const botMessage = { role: 'assistant', content: res.data.answer };
      setChatMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: 'Sorry, I had trouble processing that. Please try again.' };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setChatLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const leadsRes = await leadAPI.listPaginated({
        page: currentPage,
        limit: leadsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined
      });
      
      setLeads(leadsRes.data.leads);
      setTotalLeads(leadsRes.data.total);
      setTotalPages(leadsRes.data.total_pages);
    } catch (error) {
      toast.error('Failed to load leads');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load leads with pagination and other data in parallel
      const [
        leadsRes, remindersRes, calendarRes, projectsRes,
        teamRes, stagesRes, escSettingsRes, overdueRes
      ] = await Promise.all([
        leadAPI.listPaginated({ page: 1, limit: leadsPerPage }),
        crmAPI.getReminders({ status: 'pending' }),
        crmAPI.getCalendarEvents({}),
        projectAPI.list(),
        crmAPI.getTeamMembers().catch(() => ({ data: [] })),
        crmAPI.getPipelineStages().catch(() => ({ data: [] })),
        crmAPI.getEscalationSettings().catch(() => ({ data: null })),
        crmAPI.getOverdueLeads().catch(() => ({ data: [] }))
      ]);
      
      setLeads(leadsRes.data.leads);
      setTotalLeads(leadsRes.data.total);
      setTotalPages(leadsRes.data.total_pages);
      setCurrentPage(1);
      setReminders(remindersRes.data);
      setCalendarEvents(calendarRes.data);
      setProjects(projectsRes.data);
      setTeamMembers(teamRes.data || []);
      setPipelineStages(stagesRes.data || []);
      setEscalationSettings(escSettingsRes.data);
      setEscalationForm({
        days_before_escalation: escSettingsRes.data?.days_before_escalation || 3,
        send_email: escSettingsRes.data?.send_email ?? true,
        send_notification: escSettingsRes.data?.send_notification ?? true,
        is_active: escSettingsRes.data?.is_active ?? true
      });
      setOverdueLeads(overdueRes.data || []);
    } catch (error) {
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const loadKanbanData = async () => {
    try {
      const res = await crmAPI.getLeadsKanban();
      setKanbanData(res.data);
      setPipelineStages(res.data.stages);
    } catch (error) {
      toast.error('Failed to load Kanban data');
    }
  };

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.phone) {
      toast.error('Name and phone are required');
      return;
    }
    try {
      await leadAPI.create(newLead);
      toast.success('Lead created successfully');
      setAddLeadDialog(false);
      setNewLead({ name: '', phone: '', email: '', source: '', project_id: '', bhk_preference: '', budget: '', remarks: '' });
      loadData();
      if (viewMode === 'kanban') loadKanbanData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    }
  };

  const openLeadDetails = async (lead) => {
    try {
      const detailsRes = await crmAPI.getLeadDetails(lead.id);
      setLeadDetailDialog({ open: true, lead, details: detailsRes.data });
      
      // Reset lead-specific Effy messages
      setLeadEffyMessages([]);
      
      // Load collaterals for this lead
      try {
        const collateralsRes = await collateralAPI.getForLead(lead.id);
        setLeadCollaterals(collateralsRes.data);
      } catch (e) {
        console.log('Could not load collaterals');
        setLeadCollaterals([]);
      }
      
      // Load sales enablement if lead has a project
      const projectId = lead.project_id || detailsRes.data?.project?.id;
      if (projectId) {
        try {
          const enablementRes = await salesEnablementAPI.getForProject(projectId);
          setLeadSalesEnablement(enablementRes.data);
        } catch (e) {
          console.log('Could not load sales enablement');
          setLeadSalesEnablement(null);
        }
      } else {
        setLeadSalesEnablement(null);
      }
    } catch (error) {
      toast.error('Failed to load lead details');
    }
  };
  
  const handleShareCollateral = async (leadId, collateralId, shareVia) => {
    try {
      const res = await collateralAPI.share(leadId, collateralId, shareVia);
      const shareData = res.data;
      
      if (shareVia === 'whatsapp') {
        const message = encodeURIComponent(shareData.share_message);
        const phone = shareData.lead.contact.replace(/[^0-9]/g, '');
        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
        toast.success('Opening WhatsApp...');
      } else if (shareVia === 'email') {
        const subject = encodeURIComponent(`Check out: ${shareData.collateral.title}`);
        const body = encodeURIComponent(shareData.share_message);
        window.open(`mailto:${shareData.lead.contact}?subject=${subject}&body=${body}`, '_self');
        toast.success('Opening Email...');
      }
      
      // Refresh collaterals to show updated share history
      const collateralsRes = await collateralAPI.getForLead(leadId);
      setLeadCollaterals(collateralsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share collateral');
    }
  };
  
  const handleLeadEffyMessage = async (question, leadId) => {
    if (!question.trim()) return;
    
    const userMessage = { role: 'user', content: question };
    setLeadEffyMessages(prev => [...prev, userMessage]);
    setLeadEffyLoading(true);
    
    try {
      const res = await crmAPI.askChatbot(question, leadId);
      const botMessage = { role: 'assistant', content: res.data.answer };
      setLeadEffyMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: 'Sorry, I had trouble processing that. Please try again.' };
      setLeadEffyMessages(prev => [...prev, errorMessage]);
    } finally {
      setLeadEffyLoading(false);
    }
  };

  const handleCreateActivity = async () => {
    if (!newActivity.title) {
      toast.error('Please enter activity title');
      return;
    }
    try {
      await crmAPI.createActivity(activityDialog.leadId, newActivity);
      toast.success('Activity logged');
      setActivityDialog({ open: false, leadId: null });
      setNewActivity({ activity_type: 'call', title: '', description: '', outcome: '', duration_minutes: '', call_type: 'outgoing', phone_used: '' });
      if (leadDetailDialog.lead?.id === activityDialog.leadId) {
        const detailsRes = await crmAPI.getLeadDetails(activityDialog.leadId);
        setLeadDetailDialog(prev => ({ ...prev, details: detailsRes.data }));
      }
      loadData();
    } catch (error) {
      toast.error('Failed to log activity');
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.title || !newReminder.due_date) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await crmAPI.createReminder({ lead_id: reminderDialog.leadId, ...newReminder });
      toast.success('Reminder created');
      setReminderDialog({ open: false, leadId: null });
      setNewReminder({ reminder_type: 'follow_up', title: '', description: '', due_date: '', priority: 'medium' });
      loadData();
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await crmAPI.completeReminder(reminderId);
      toast.success('Reminder completed');
      loadData();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleDismissReminder = async (reminderId) => {
    try {
      await crmAPI.dismissReminder(reminderId);
      toast.success('Reminder dismissed');
      loadData();
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const handleUpdateDisposition = async () => {
    if (!dispositionForm.newStatus) {
      toast.error('Please select a status');
      return;
    }
    try {
      if (dispositionForm.createReminder && dispositionForm.reminderTitle && dispositionForm.reminderDueDate) {
        await crmAPI.updateDispositionWithReminder(dispositionDialog.lead?.id, {
          lead_id: dispositionDialog.lead?.id,
          new_status: dispositionForm.newStatus,
          notes: dispositionForm.notes,
          create_reminder: true,
          reminder_title: dispositionForm.reminderTitle,
          reminder_due_date: dispositionForm.reminderDueDate,
          reminder_type: dispositionForm.reminderType,
          reminder_priority: dispositionForm.reminderPriority
        });
        toast.success('Status updated with reminder');
      } else {
        await crmAPI.updateDisposition(dispositionDialog.lead?.id, dispositionForm.newStatus, dispositionForm.notes);
        toast.success('Lead status updated');
      }
      setDispositionDialog({ open: false, lead: null });
      setDispositionForm({ newStatus: '', notes: '', createReminder: false, reminderTitle: '', reminderDueDate: '', reminderType: 'follow_up', reminderPriority: 'medium' });
      loadData();
      if (viewMode === 'kanban') loadKanbanData();
      if (leadDetailDialog.lead?.id === dispositionDialog.lead?.id) {
        const detailsRes = await crmAPI.getLeadDetails(dispositionDialog.lead?.id);
        setLeadDetailDialog(prev => ({ ...prev, details: detailsRes.data }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReassignLead = async () => {
    if (!reassignForm.newAssigneeId) {
      toast.error('Please select a team member');
      return;
    }
    try {
      await crmAPI.reassignLead(reassignDialog.lead?.id, reassignForm.newAssigneeId, reassignForm.notes);
      toast.success('Lead reassigned successfully');
      setReassignDialog({ open: false, lead: null });
      setReassignForm({ newAssigneeId: '', notes: '' });
      loadData();
      if (leadDetailDialog.lead?.id === reassignDialog.lead?.id) {
        const detailsRes = await crmAPI.getLeadDetails(reassignDialog.lead?.id);
        setLeadDetailDialog(prev => ({ ...prev, details: detailsRes.data }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign lead');
    }
  };

  const handleMoveLeadStage = async (leadId, newStage) => {
    try {
      await crmAPI.moveLeadStage(leadId, newStage);
      loadKanbanData();
      toast.success('Lead moved');
    } catch (error) {
      toast.error('Failed to move lead');
    }
  };

  const handleSaveStage = async () => {
    if (!newStage.name) {
      toast.error('Stage name is required');
      return;
    }
    try {
      if (stageDialog.isEdit && stageDialog.stage) {
        await crmAPI.updatePipelineStage(stageDialog.stage.id, newStage);
        toast.success('Stage updated');
      } else {
        await crmAPI.createPipelineStage(newStage);
        toast.success('Stage created');
      }
      setStageDialog({ open: false, stage: null, isEdit: false });
      setNewStage({ name: '', color: '#3b82f6', is_win_stage: false, is_lost_stage: false });
      loadKanbanData();
      loadData();
    } catch (error) {
      toast.error('Failed to save stage');
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!confirm('Are you sure you want to delete this stage?')) return;
    try {
      await crmAPI.deletePipelineStage(stageId);
      toast.success('Stage deleted');
      loadKanbanData();
      loadData();
    } catch (error) {
      toast.error('Failed to delete stage');
    }
  };

  const handleSaveEscalationSettings = async () => {
    try {
      await crmAPI.updateEscalationSettings(escalationForm);
      toast.success('Escalation settings saved');
      setSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleCheckEscalations = async () => {
    try {
      const res = await crmAPI.checkEscalations();
      toast.success(`Checked escalations: ${res.data.escalated} leads escalated`);
      loadData();
    } catch (error) {
      toast.error('Failed to check escalations');
    }
  };

  const getStageConfig = (status) => {
    const stage = pipelineStages.find(s => s.slug === status);
    if (stage) return { label: stage.name, color: stage.color };
    const fallbackMap = {
      new: { label: 'New', color: '#3b82f6' },
      contacted: { label: 'Contacted', color: '#06b6d4' },
      interested: { label: 'Interested', color: '#10b981' },
      qualified: { label: 'Qualified', color: '#10b981' },
      site_visit: { label: 'Site Visit', color: '#f59e0b' },
      site_visit_scheduled: { label: 'Site Visit', color: '#f59e0b' },
      site_visit_completed: { label: 'Site Visit Done', color: '#f97316' },
      negotiation: { label: 'Negotiation', color: '#8b5cf6' },
      booked: { label: 'Booked', color: '#22c55e' },
      lost: { label: 'Lost', color: '#ef4444' },
    };
    return fallbackMap[status] || { label: status, color: '#6b7280' };
  };

  const getSourceLabel = (source) => {
    return LEAD_SOURCES.find(s => s.value === source)?.label || source || 'Unknown';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd MMM yyyy');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr), 'dd MMM yyyy, hh:mm a');
  };

  // Filter leads - only client-side filtering for source and overdue (search and status are server-side)
  const filteredLeads = leads.filter(lead => {
    const matchesSource = sourceFilter === 'all' || lead.source === sourceFilter;
    const matchesOverdue = !showOverdueOnly || overdueLeads.some(o => o.id === lead.id);
    return matchesSource && matchesOverdue;
  });

  // Handle search with debounce
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) {
        loadLeads();
      }
    }, 300); // 300ms debounce
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle status filter change
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const todayReminders = reminders.filter(r => {
    const due = new Date(r.due_date);
    const today = new Date();
    return due.toDateString() === today.toDateString();
  });

  const overdueReminders = reminders.filter(r => r.is_overdue);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="crm-page-loading">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-7 w-7 text-indigo-600" />
            Lead CRM
          </h1>
          <p className="text-muted-foreground">Manage leads, follow-ups, and conversions</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setAddLeadDialog(true)} data-testid="add-lead-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setApiKeySettingsDialog(true)} 
            data-testid="ai-settings-btn"
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
          >
            <Bot className="h-4 w-4 mr-2" />
            AI Settings
          </Button>
          {isAdmin && (
            <>
              <Button variant="outline" onClick={() => setStageManagerDialog(true)} data-testid="manage-stages-btn">
                <Kanban className="h-4 w-4 mr-2" />
                Stages
              </Button>
              <Button variant="outline" onClick={() => setSettingsDialog(true)} data-testid="settings-btn">
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            </>
          )}
          <Button onClick={loadData} variant="outline" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card data-testid="stat-total-leads">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="h-4 w-4" />
              <span className="text-sm">Total Leads</span>
            </div>
            <p className="text-2xl font-bold">{totalLeads}</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-today-followups">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <Bell className="h-4 w-4" />
              <span className="text-sm">Today's Follow-ups</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">{todayReminders.length}</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer ${showOverdueOnly ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setShowOverdueOnly(!showOverdueOnly)}
          data-testid="stat-overdue"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">Overdue</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{overdueLeads.length}</p>
            {showOverdueOnly && <p className="text-xs text-red-500 mt-1">Filter active</p>}
          </CardContent>
        </Card>
        <Card data-testid="stat-booked">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-emerald-600 mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">Booked</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">
              {leads.filter(l => l.status === 'booked').length}
            </p>
          </CardContent>
        </Card>
        <Card data-testid="stat-lost">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-gray-600 mb-1">
              <X className="h-4 w-4" />
              <span className="text-sm">Lost</span>
            </div>
            <p className="text-2xl font-bold text-gray-700">
              {leads.filter(l => l.status === 'lost').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <TabsList>
            <TabsTrigger value="leads" data-testid="tab-leads">
              <Users className="h-4 w-4 mr-2" />
              Leads ({totalLeads})
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders">
              <Bell className="h-4 w-4 mr-2" />
              Reminders ({reminders.length})
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar">
              <CalendarIcon className="h-4 w-4 mr-2" />
              Calendar
            </TabsTrigger>
          </TabsList>

          {activeTab === 'leads' && (
            <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
              <Button 
                variant={viewMode === 'list' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="h-4 w-4 mr-1" />
                List
              </Button>
              <Button 
                variant={viewMode === 'kanban' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('kanban')}
                data-testid="view-kanban-btn"
              >
                <Kanban className="h-4 w-4 mr-1" />
                Kanban
              </Button>
            </div>
          )}
        </div>

        {/* Leads Tab */}
        <TabsContent value="leads" className="mt-4 space-y-4">
          {viewMode === 'list' ? (
            <LeadsListView 
              leads={filteredLeads}
              overdueLeads={overdueLeads}
              searchTerm={searchTerm}
              setSearchTerm={handleSearchChange}
              statusFilter={statusFilter}
              setStatusFilter={handleStatusFilterChange}
              sourceFilter={sourceFilter}
              setSourceFilter={setSourceFilter}
              showOverdueOnly={showOverdueOnly}
              setShowOverdueOnly={setShowOverdueOnly}
              pipelineStages={pipelineStages}
              getStageConfig={getStageConfig}
              getSourceLabel={getSourceLabel}
              formatDate={formatDate}
              openLeadDetails={openLeadDetails}
              setActivityDialog={setActivityDialog}
              setReminderDialog={setReminderDialog}
              // Pagination props
              currentPage={currentPage}
              totalPages={totalPages}
              totalLeads={totalLeads}
              onPageChange={handlePageChange}
            />
          ) : (
            <KanbanBoard 
              kanbanData={kanbanData}
              pipelineStages={pipelineStages}
              overdueLeads={overdueLeads}
              openLeadDetails={openLeadDetails}
              handleMoveLeadStage={handleMoveLeadStage}
              setStageDialog={setStageDialog}
              setNewStage={setNewStage}
              handleDeleteStage={handleDeleteStage}
              isAdmin={isAdmin}
              formatDate={formatDate}
              onCallLead={(lead) => setCallAssistantDialog({ open: true, lead })}
            />
          )}
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="mt-4 space-y-4">
          {overdueReminders.length > 0 && (
            <div>
              <h3 className="font-semibold text-red-600 flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5" />
                Overdue ({overdueReminders.length})
              </h3>
              <div className="space-y-2">
                {overdueReminders.map(reminder => (
                  <ReminderCard 
                    key={reminder.id} 
                    reminder={reminder} 
                    onComplete={handleCompleteReminder}
                    onDismiss={handleDismissReminder}
                    isOverdue
                  />
                ))}
              </div>
            </div>
          )}

          {todayReminders.length > 0 && (
            <div>
              <h3 className="font-semibold text-amber-600 flex items-center gap-2 mb-3">
                <Clock className="h-5 w-5" />
                Today ({todayReminders.length})
              </h3>
              <div className="space-y-2">
                {todayReminders.filter(r => !r.is_overdue).map(reminder => (
                  <ReminderCard 
                    key={reminder.id} 
                    reminder={reminder} 
                    onComplete={handleCompleteReminder}
                    onDismiss={handleDismissReminder}
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <CalendarIcon className="h-5 w-5" />
              Upcoming
            </h3>
            <div className="space-y-2">
              {reminders.filter(r => !r.is_overdue && !todayReminders.includes(r)).map(reminder => (
                <ReminderCard 
                  key={reminder.id} 
                  reminder={reminder} 
                  onComplete={handleCompleteReminder}
                  onDismiss={handleDismissReminder}
                />
              ))}
            </div>
          </div>

          {reminders.length === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Bell className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No pending reminders</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="mt-4">
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="p-4">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  className="rounded-md border"
                />
              </CardContent>
            </Card>
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Events on {format(selectedDate, 'dd MMMM yyyy')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {calendarEvents.filter(e => {
                    const eventDate = new Date(e.date);
                    return eventDate.toDateString() === selectedDate.toDateString();
                  }).length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No events scheduled</p>
                  ) : (
                    <div className="space-y-3">
                      {calendarEvents.filter(e => {
                        const eventDate = new Date(e.date);
                        return eventDate.toDateString() === selectedDate.toDateString();
                      }).map(event => (
                        <div 
                          key={event.id} 
                          className="flex items-center gap-4 p-3 rounded-lg border"
                          style={{ borderLeftColor: event.color, borderLeftWidth: '4px' }}
                        >
                          <div className="flex-1">
                            <p className="font-medium">{event.title}</p>
                            <p className="text-sm text-muted-foreground">
                              {event.type === 'site_visit' ? `Project: ${event.project?.name}` : `Lead: ${event.lead?.name}`}
                            </p>
                          </div>
                          {event.time && <Badge variant="outline">{event.time}</Badge>}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddLeadDialog 
        open={addLeadDialog}
        onOpenChange={setAddLeadDialog}
        newLead={newLead}
        setNewLead={setNewLead}
        projects={projects}
        onSubmit={handleAddLead}
      />

      <Dialog open={leadDetailDialog.open} onOpenChange={(open) => setLeadDetailDialog({ open, lead: null, details: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {leadDetailDialog.details && (
            <LeadDetailView 
              details={leadDetailDialog.details}
              onLogActivity={(leadId) => setActivityDialog({ open: true, leadId })}
              onCreateReminder={(leadId) => setReminderDialog({ open: true, leadId })}
              onUpdateDisposition={(lead) => {
                setDispositionForm({ ...dispositionForm, newStatus: lead.status || 'new' });
                setDispositionDialog({ open: true, lead });
              }}
              onReassign={(lead) => setReassignDialog({ open: true, lead })}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
              canReassign={canReassign}
              getSourceLabel={getSourceLabel}
              pipelineStages={pipelineStages}
              getStageConfig={getStageConfig}
              collaterals={leadCollaterals}
              salesEnablement={leadSalesEnablement}
              onShareCollateral={handleShareCollateral}
              onAskEffy={handleLeadEffyMessage}
              effyMessages={leadEffyMessages}
              effyLoading={leadEffyLoading}
            />
          )}
        </DialogContent>
      </Dialog>

      <ActivityDialog 
        open={activityDialog.open}
        onOpenChange={setActivityDialog}
        leadId={activityDialog.leadId}
        newActivity={newActivity}
        setNewActivity={setNewActivity}
        onSubmit={handleCreateActivity}
      />

      <ReminderDialog 
        open={reminderDialog.open}
        onOpenChange={setReminderDialog}
        leadId={reminderDialog.leadId}
        newReminder={newReminder}
        setNewReminder={setNewReminder}
        onSubmit={handleCreateReminder}
      />

      <DispositionDialog 
        open={dispositionDialog.open}
        onOpenChange={setDispositionDialog}
        lead={dispositionDialog.lead}
        dispositionForm={dispositionForm}
        setDispositionForm={setDispositionForm}
        pipelineStages={pipelineStages}
        getStageConfig={getStageConfig}
        onSubmit={handleUpdateDisposition}
      />

      <ReassignDialog 
        open={reassignDialog.open}
        onOpenChange={setReassignDialog}
        lead={reassignDialog.lead}
        reassignForm={reassignForm}
        setReassignForm={setReassignForm}
        teamMembers={teamMembers}
        onSubmit={handleReassignLead}
      />

      <SettingsDialog 
        open={settingsDialog}
        onOpenChange={setSettingsDialog}
        escalationForm={escalationForm}
        setEscalationForm={setEscalationForm}
        onSaveSettings={handleSaveEscalationSettings}
        onCheckEscalations={handleCheckEscalations}
      />

      <StageDialog 
        open={stageDialog.open}
        onOpenChange={setStageDialog}
        stageDialog={stageDialog}
        newStage={newStage}
        setNewStage={setNewStage}
        onSubmit={handleSaveStage}
      />

      {/* Call Assistant Dialog */}
      <CallAssistantDialog
        open={callAssistantDialog.open}
        onClose={() => setCallAssistantDialog({ open: false, lead: null })}
        lead={callAssistantDialog.lead}
        pipelineStages={pipelineStages}
        onUpdateLead={loadData}
      />

      {/* AI Settings Dialog (Admin) */}
      <AdminAISettingsDialog
        open={apiKeySettingsDialog}
        onClose={() => setApiKeySettingsDialog(false)}
        onSettingsChanged={loadData}
      />

      {/* Stage Manager Dialog */}
      <StageManagerDialog
        open={stageManagerDialog}
        onClose={() => setStageManagerDialog(false)}
        stages={pipelineStages}
        onStagesUpdated={loadData}
      />

      {/* Ask Effy Floating Button */}
      <button
        onClick={() => setChatbotOpen(!chatbotOpen)}
        className="fixed bottom-20 right-4 h-14 w-14 rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center z-50"
        data-testid="ask-effy-btn"
      >
        {chatbotOpen ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      <AskEffyChatbot 
        isOpen={chatbotOpen}
        onClose={() => setChatbotOpen(false)}
        messages={chatMessages}
        input={chatInput}
        setInput={setChatInput}
        onSend={handleSendChatMessage}
        isLoading={chatLoading}
      />
    </div>
  );
};

export default CRMPage;
